import { MoreHorizontal } from 'lucide-react';
import Dropdown from './Dropdown.jsx';

/**
 * Trigger styles are locked to **non-gradient** `Button` variants on purpose:
 * `labeled` is secondary/bordered, `icon` is ghost. SelectActions is designed
 * to sit next to a primary gradient button (e.g. a "Publish" CTA) and must stay
 * visually subordinate to it — only one gradient per cluster (CT-5761). The
 * gradient / primary `Button` variants are intentionally **not** reachable
 * through this component.
 */
const TRIGGER_VARIANT = {
  labeled: 'secondary',
  icon: 'ghost',
};

/**
 * Standardised **Actions** control for the AIMS OS design system.
 *
 * A thin wrapper over {@link Dropdown} that fixes the trigger and its variants
 * so actions menus look and behave the same everywhere. The trigger is EITHER a
 * labeled, bordered "Actions ▾" button (`variant="labeled"`, the default) or an
 * icon-only "…" button (`variant="icon"`). Each entry in `actions` becomes a
 * `Dropdown.Item` with an optional leading Lucide icon, a `danger` (red) tone
 * and a `disabled` state; `{ separator: true }` renders a divider.
 *
 * Keyboard and screen-reader support (aria-haspopup + aria-expanded on the
 * trigger, `role="menu"`/`menuitem`, arrow-key roving focus that skips disabled
 * rows, Escape to close and return focus to the trigger) is inherited from
 * `Dropdown`. The trigger is never a gradient, so it does not compete with a
 * sibling primary gradient button.
 *
 * @param {object} props
 * @param {'labeled'|'icon'} [props.variant='labeled'] - Trigger style.
 * @param {string} [props.label='Actions'] - Visible label (labeled) / accessible name (icon).
 * @param {Array<{id: string, label?: React.ReactNode, icon?: import('lucide-react').LucideIcon, danger?: boolean, disabled?: boolean, separator?: boolean, onSelect?: (event: MouseEvent) => void}>} [props.actions=[]] - Menu entries, in render order.
 * @param {'left'|'right'} [props.align='right'] - Menu alignment relative to the trigger.
 * @param {'top'|'bottom'} [props.menuPosition='bottom'] - Open upward or downward.
 * @param {'small'|'medium'|'large'|'sm'|'md'|'lg'} [props.size='medium'] - Trigger size.
 * @param {import('lucide-react').LucideIcon} [props.icon=MoreHorizontal] - Icon for the icon-only trigger.
 * @param {boolean} [props.disabled=false] - Disables the trigger.
 * @param {boolean} [props.usePortal=false] - Render the menu in a portal to avoid clipping.
 * @param {(open: boolean) => void} [props.onOpenChange] - Notified when the menu opens or closes.
 * @param {string} [props.className] - Class applied to the root wrapper.
 * @param {string} [props.menuClassName] - Class applied to the menu panel.
 */
const SelectActions = ({
  variant = 'labeled',
  label = 'Actions',
  actions = [],
  align = 'right',
  menuPosition = 'bottom',
  size = 'medium',
  icon: TriggerIcon = MoreHorizontal,
  disabled = false,
  usePortal = false,
  onOpenChange,
  className = '',
  menuClassName = '',
}) => {
  const isIcon = variant === 'icon';

  return (
    <Dropdown
      label={isIcon ? undefined : label}
      triggerIcon={isIcon ? TriggerIcon : undefined}
      triggerAriaLabel={label}
      variant={TRIGGER_VARIANT[variant] || TRIGGER_VARIANT.labeled}
      size={size}
      alignMenu={align}
      menuPosition={menuPosition}
      disabled={disabled}
      usePortal={usePortal}
      onOpenChange={onOpenChange}
      className={className}
      menuClassName={menuClassName}
    >
      {actions.map((action) =>
        action.separator ? (
          <Dropdown.Separator key={action.id} />
        ) : (
          <Dropdown.Item
            key={action.id}
            icon={action.icon}
            variant={action.danger ? 'danger' : 'default'}
            disabled={action.disabled}
            onClick={action.onSelect}
          >
            {action.label}
          </Dropdown.Item>
        ),
      )}
    </Dropdown>
  );
};

export default SelectActions;
